import React from 'react';
import { formatFullMoney } from '../../utils/dealerListSummaryUtils';

const SummaryMeta = ({ icon, children }) => (
  <div className="dealer-summary-meta">
    <i className={`fas ${icon}`}></i>
    <span>{children}</span>
  </div>
);

const BalanceBadge = ({ type, label, amount, currency = 'TRY' }) => {
  if (type === 'balanced' || !amount) {
    return <span className="dealer-summary-badge balanced"><i className="fas fa-check-circle"></i> Dengede</span>;
  }
  if (type === 'credit') {
    return (
      <span className="dealer-summary-badge credit">
        <i className="fas fa-hand-holding-usd"></i>
        Alacaklı {formatFullMoney(amount, currency)}
      </span>
    );
  }
  return (
    <span className="dealer-summary-badge debt">
      <i className="fas fa-hourglass-half"></i>
      {label} {formatFullMoney(amount, currency)}
    </span>
  );
};

const BayiSummary = ({ summary }) => {
  if (!summary?.hasData) {
    return (
      <div className="dealer-summary-empty">
        <i className="fas fa-clipboard"></i>
        <span>Henüz sipariş listesi yok</span>
      </div>
    );
  }

  return (
    <div className="dealer-summary-block bayi-block">
      <div className="dealer-summary-top">
        <SummaryMeta icon="fa-clipboard-list">
          {summary.sessionCount} liste
          {summary.activeCount > 0 && (
            <span className="dealer-summary-pill active">{summary.activeCount} aktif</span>
          )}
        </SummaryMeta>
        {summary.lastDateLabel && (
          <SummaryMeta icon="fa-calendar-day">Son: {summary.lastDateLabel}</SummaryMeta>
        )}
      </div>
      <div className="dealer-summary-amounts">
        <span className="dealer-summary-amount total" title="Toplam sipariş tutarı">
          {formatFullMoney(summary.grandTotal)}
        </span>
        <span className="dealer-summary-divider">/</span>
        <span className="dealer-summary-amount paid" title="Tahsil edilen">
          {formatFullMoney(summary.grandPaid)}
        </span>
      </div>
      <BalanceBadge
        type={summary.isCreditor ? 'credit' : summary.isBalanced ? 'balanced' : 'debt'}
        label={summary.balanceLabel}
        amount={summary.isCreditor ? summary.creditBalance : summary.openBalance}
      />
    </div>
  );
};

const SupplierSummary = ({ summary }) => {
  if (!summary?.hasData) {
    return (
      <div className="dealer-summary-empty supplier">
        <i className="fas fa-truck-loading"></i>
        <span>Henüz alım listesi yok</span>
      </div>
    );
  }

  return (
    <div className="dealer-summary-block supplier-block">
      <div className="dealer-summary-top">
        <SummaryMeta icon="fa-boxes">
          {summary.sessionCount} alım listesi
        </SummaryMeta>
        {summary.lastDateLabel && (
          <SummaryMeta icon="fa-calendar-day">Son: {summary.lastDateLabel}</SummaryMeta>
        )}
      </div>
      <div className="dealer-summary-currency-lines">
        {summary.currencyLines.map((line) => (
          <div key={line.currency} className="dealer-summary-currency-line">
            <span className="dealer-summary-currency-code">{line.currency}</span>
            <span className="dealer-summary-amount total">{formatFullMoney(line.total, line.currency)}</span>
            {line.isCreditor ? (
              <>
                <span className="dealer-summary-divider">·</span>
                <span className="dealer-summary-amount paid">{formatFullMoney(line.creditBalance, line.currency)} alacak</span>
              </>
            ) : (
              <>
                <span className="dealer-summary-divider">·</span>
                <span className="dealer-summary-amount debt">{formatFullMoney(line.remaining, line.currency)} borç</span>
              </>
            )}
          </div>
        ))}
      </div>
      {summary.currencyLines.length > 1 && summary.totalRemainingTry > 0 && (
        <span className="dealer-summary-badge debt subtle">
          TL karşılığı borç {formatFullMoney(summary.totalRemainingTry)}
        </span>
      )}
      {summary.currencyLines.length > 1 && summary.isCreditor && summary.totalCreditTry > 0 && (
        <span className="dealer-summary-badge credit subtle">
          TL karşılığı alacak {formatFullMoney(summary.totalCreditTry)}
        </span>
      )}
    </div>
  );
};

const DealerOrderSummaryCell = ({ summary, isLoading, firmType }) => {
  if (isLoading) {
    return (
      <div className="dealer-summary-skeleton" aria-hidden="true">
        <span className="dealer-summary-skeleton-line wide"></span>
        <span className="dealer-summary-skeleton-line"></span>
        <span className="dealer-summary-skeleton-line short"></span>
      </div>
    );
  }

  if (!summary) {
    return <span className="dealer-summary-dash">—</span>;
  }

  const type = firmType || summary.firmType || 'bayi';

  if (type === 'bayi') {
    return <BayiSummary summary={summary.bayi} />;
  }

  if (type === 'tedarikci') {
    return <SupplierSummary summary={summary.supplier} />;
  }

  return (
    <div className="dealer-summary-dual">
      <div className="dealer-summary-dual-section">
        <span className="dealer-summary-dual-label bayi"><i className="fas fa-store"></i> Bayi</span>
        <BayiSummary summary={summary.bayi} />
      </div>
      <div className="dealer-summary-dual-section">
        <span className="dealer-summary-dual-label supplier"><i className="fas fa-truck"></i> Tedarikçi</span>
        <SupplierSummary summary={summary.supplier} />
      </div>
    </div>
  );
};

export default DealerOrderSummaryCell;
